/**
 * Choosing a skin in the Inspector's Skin row. A skin is a whole model, so
 * this is an ordinary edit of the asset prop, one undo step over the
 * selection, like picking the model in the Mesh field.
 *
 * What plays carries over where it can: a clip field that reads its clips
 * from that asset prop (a `clipRef` whose `source` names it, as the
 * Animator's Anim to play does) keeps its clip when the new model has one of
 * that name, and otherwise goes back to empty, which plays the first clip,
 * instead of leaving a missing-clip warning. Found through the schemas, so
 * no component is named here (rule 4).
 */
import type { CommandOrigin, CommandRequest } from "../../commands/types";
import type { EntityRecord } from "../../world/entity";
import type { ComponentRegistry } from "../../world/schema/ComponentRegistry";
import type { ClipSource } from "../../world/schema/propTypes";
import type { RequestGroup } from "../selection/selectionEdits";

export interface SkinEditInput {
  readonly entities: readonly EntityRecord[];
  /** The asset prop the Skin row edits. */
  readonly target: ClipSource;
  readonly skin: { readonly model: string; readonly name: string };
  readonly components: ComponentRegistry;
  /** A model's clip names (the asset catalog's). */
  readonly clipsOf: (path: string) => readonly string[];
  readonly origin: CommandOrigin;
}

export function skinEdit(input: SkinEditInput): RequestGroup {
  const clips = input.clipsOf(input.skin.model);
  const clipFields = clipFieldsReading(input.components, input.target);

  const requests = input.entities.flatMap((entity) => {
    const set = (component: string, prop: string, value: unknown): CommandRequest => ({
      type: "entity.setProperty",
      payload: { entity: entity.id, component, prop, value },
      origin: input.origin,
    });

    const cleared = clipFields.filter(({ component, prop }) => {
      const clip = entity.components.get(component)?.[prop];
      return typeof clip === "string" && clip !== "" && !clips.includes(clip);
    });
    return [
      set(input.target.component, input.target.prop, input.skin.model),
      ...cleared.map(({ component, prop }) => set(component, prop, "")),
    ];
  });

  return { label: `Set skin ${input.skin.name}`, requests };
}

/** Every `clipRef` prop, on any component, whose clips come from the target asset prop. */
function clipFieldsReading(components: ComponentRegistry, target: ClipSource): ClipSource[] {
  return components
    .all()
    .flatMap((schema) =>
      Object.entries(schema.props).flatMap(([prop, type]) =>
        type.type === "clipRef" &&
        type.source.component === target.component &&
        type.source.prop === target.prop
          ? [{ component: schema.type, prop }]
          : [],
      ),
    );
}
